/**
 * ONE TAP — player movement simulation.
 *
 * This file is the literal basis of client-side prediction: the browser and the
 * Durable Object both call `stepMovement` with the same (player, command, world)
 * and must land on identical results. Therefore:
 *
 *   - no Math.random(), no Date.now(), no wall clock, no DOM, no `three`
 *   - every mutation lands on `p` (PlayerState) or is pushed into `events`
 *   - nothing survives between calls in module scope; scratch is write-before-read
 *   - all timing is driven by `cmd.dt`, never by a frame delta
 *
 * Coordinate convention: +Y is up and `p.pos` is the CAPSULE BASE (the player's
 * feet). PLAYER_EYE_HEIGHT and HEAD_HEIGHT are documented as offsets from the
 * capsule base, so every trace here sweeps a capsule whose base sits at `pos` and
 * whose top sits at `pos.y + height`.
 *
 * The four traversal verbs (slide/surf, dash + double jump, wall-run, grapple) all
 * live in one tick, ordered so they chain into each other: grapple -> wall-run ->
 * jump -> dash -> slide -> accelerate -> gravity -> move -> land. Momentum is only
 * ever redirected, never silently deleted (DESIGN.md §3).
 */

import {
  AIR_ACCEL,
  AIR_FRICTION,
  AIR_SPEED_CAP,
  AIR_STRAFE_ACCEL,
  COYOTE_TIME_MS,
  DASH_CHARGES,
  DASH_CHARGES_UNARMED,
  DASH_DURATION_MS,
  DASH_GROUND_REFILL,
  DASH_RECHARGE_MS,
  DASH_SPEED,
  DASH_TO_SLIDE_BONUS,
  DASH_TO_SLIDE_WINDOW_MS,
  DASH_VERTICAL_DAMP,
  DOUBLE_JUMP_VELOCITY,
  GRAPPLE_AIM_CONE,
  GRAPPLE_NONE,
  GRAPPLE_SURFACE,
  GRAPPLE_COOLDOWN_MS,
  GRAPPLE_DAMPING,
  GRAPPLE_HOOK_SPEED,
  GRAPPLE_MAX_MS,
  GRAPPLE_MAX_SPEED,
  GRAPPLE_MIN_LENGTH,
  GRAPPLE_PULL_ACCEL,
  GRAPPLE_RANGE,
  GRAPPLE_RELEASE_BOOST,
  GRAPPLE_RELEASE_WINDOW_MS,
  GRAPPLE_SPRING_K,
  GRAVITY,
  GROUND_ACCEL,
  GROUND_FRICTION,
  JUMP_BUFFER_MS,
  JUMP_VELOCITY,
  MAX_GROUND_SLOPE,
  PITCH_LIMIT,
  PLAYER_CROUCH_EYE_HEIGHT,
  PLAYER_CROUCH_HEIGHT,
  PLAYER_EYE_HEIGHT,
  PLAYER_HEIGHT,
  PLAYER_RADIUS,
  RUN_SPEED,
  SLIDE_BOOST,
  SLIDE_FRICTION,
  SLIDE_MAX_SPEED,
  SLIDE_MIN_MS,
  SLIDE_MIN_SPEED,
  SLIDE_SLOPE_ACCEL,
  SLIDE_STEER_RATE,
  STEP_HEIGHT,
  STOP_SPEED,
  SURF_LAND_GRACE_MS,
  SURF_LAND_REFUND,
  UNARMED_SPEED_MULT,
  WALLJUMP_FORWARD_KEEP,
  WALLJUMP_PUSH_VELOCITY,
  WALLJUMP_UP_VELOCITY,
  WALLRUN_ACCEL,
  WALLRUN_CAMERA_ROLL,
  WALLRUN_GRAVITY_SCALE,
  WALLRUN_MAX_MS,
  WALLRUN_MAX_SPEED,
  WALLRUN_MAX_WALL_TILT,
  WALLRUN_MIN_HEIGHT,
  WALLRUN_MIN_SPEED,
  WALLRUN_REATTACH_COOLDOWN_MS,
  WALLRUN_SAME_SURFACE_LOCKOUT,
  WALLRUN_STICK_FORCE,
  WOUNDED_SPEED_MULT,
} from '@shared/constants';
import {
  EPSILON,
  addScaled,
  clamp,
  clamp01,
  clipVelocity,
  copy,
  cross,
  dirFromYawPitch,
  distance,
  dot,
  forwardFromYaw,
  length,
  lengthSq,
  lengthXZ,
  lerpN,
  normalize,
  projectOnPlane,
  rightFromYaw,
  scale,
  set,
  smoothstep,
  sub,
  v3,
  type Vec3,
} from '@shared/math';
import {
  Btn,
  HealthState,
  HitZone,
  MoveState,
  SurfaceKind,
  hasBtn,
  type ArenaData,
  type CollisionWorld,
  type GameEvent,
  type InputCommand,
  type PlayerState,
  type RaycastResult,
  type TraceResult,
} from '@shared/types';

// ---------------------------------------------------------------------------
// Numerical constants.
//
// These are solver epsilons and probe distances — they exist so the collision
// response is stable, not so a designer can tune the feel. Anything that changes
// how the game *feels* lives in @shared/constants.
// ---------------------------------------------------------------------------

/** A surface this steep or shallower can be stood on. */
const GROUND_NORMAL_MIN = Math.cos(MAX_GROUND_SLOPE);
/** |normal.y| at or below this means the surface is within WALLRUN_MAX_WALL_TILT of vertical. */
const WALL_TILT_SIN = Math.sin(WALLRUN_MAX_WALL_TILT);
/** Pushed this far out of every plane we touch so the next trace never starts solid. */
const SURFACE_OFFSET = 0.002;
/** Quake's clip overbounce — slightly above 1 so we drift off planes instead of onto them. */
const OVERBOUNCE = 1.001;
/** Downward probe used to answer "am I standing on something". */
const GROUND_PROBE = 0.06;
/** Upward velocity above which you are definitively not standing (covers overbounce residue). */
const GROUND_VY_TOLERANCE = 0.5;
/** How far to the side we sweep looking for a runnable wall, beyond the capsule radius. */
const WALL_PROBE_DIST = 0.35;
/**
 * Reach used to HOLD a wall-run, as opposed to start one. Deliberately much longer:
 * a rooftop facade is not a plane, it is pilasters, setbacks and window reveals, and
 * a probe that only sees 35 cm drops you every time the wall steps away by half a
 * metre. The stick force pulls you back onto the new face, so following it is both
 * correct and what the player expects. Entry stays tight so you never get glued to
 * something you were only passing.
 */
const WALL_HOLD_PROBE_DIST = 1.0;
/** First push distance when digging the capsule out of a solid; grows each iteration. */
const UNSTICK_STEP = 0.012;
/** How many shallowest-axis pushes to try before falling back to the direction search. */
const UNSTICK_ITERS = 8;
/** Ladder of distances the last-resort unstick search tries, nearest first. */
const UNSTICK_DISTANCES = [0.06, 0.14, 0.3, 0.6, 1.0];
/** Hard ceiling on a single simulated step, so one enormous dt can never tunnel a player. */
const MAX_STEP_DT = 0.1;
/** Sanity clamp; a NaN or a physics explosion must never escape this function. */
const MAX_SANE_SPEED = 220;
/**
 * Max collision-response iterations per move.
 *
 * Every bump is one more plane the solver is allowed to slide along, and an unstick
 * retry spends one too. Four was tight enough that a player crossing a seam between
 * three brushes ran out of iterations mid-tick and simply stopped.
 */
const MAX_BUMPS = 6;
/**
 * Air step-up (mantle) fires while you are at or past the apex. Rising into a lip is
 * a jump you mistimed and should read as one; falling onto it is a mantle, and every
 * game that feels good lets you have it.
 */
const AIR_STEP_MAX_RISE = 0.5;
/** Alternating-projection passes used when three or more planes fight over one velocity. */
const CLIP_RELAX_PASSES = 4;
/** Wall-run camera roll eases in over this long so the horizon never snaps. */
const WALLRUN_ROLL_RAMP_MS = 140;
/** Peak camera roll from hard strafing, as a fraction of the wall-run roll. */
const STRAFE_ROLL_FRACTION = 0.28;
/** |dot(right, wallNormal)| at which the wall-run lean reaches full tilt. */
const WALLRUN_ROLL_SIDE_SOFT = 0.35;
/** Below this fraction of WALLRUN_MIN_SPEED a wall-run peters out and detaches. */
const WALLRUN_KEEP_SPEED_FRAC = 0.6;
/**
 * dot(velDir, -wallNormal) above this means you are running INTO the wall, not along it.
 *
 * Was 0.72, i.e. anything steeper than a 44 deg approach was refused outright — you
 * bounced off and fell. But a steep approach is the one a player makes on purpose when
 * they want to convert a sprint into a climb, and refusing it is unreadable: the wall
 * is right there and nothing happens. 0.92 accepts up to a 67 deg approach and lets
 * the entry redirect the momentum instead of eating it.
 */
const WALLRUN_MAX_INTO_WALL = 0.92;
/**
 * Movement-stick gate. Entry only asks that you are not actively pulling AWAY from the
 * wall; it used to demand a positive forward stick, which quietly refused every
 * strafe-led approach — including the one right after a wall-jump, when your view has
 * already swung to the next wall and your hands are on A or D.
 */
const WALLRUN_MIN_FORWARD = 0.15;
/**
 * Entry speed floor as a fraction of WALLRUN_MIN_SPEED, applied to the ALONG-WALL
 * speed. By the time you are touching a wall the collision response has already eaten
 * the into-wall half of your velocity, so testing raw speed against the full threshold
 * punishes you twice for the same approach.
 */
const WALLRUN_ENTRY_SPEED_FRAC = 0.72;
/** Extra reach on the eye-height "is this wall taller than me" ray. */
const WALL_HEAD_SLACK = 0.2;
/** Fraction of the wall-run's downward speed kept when you first stick to the wall. */
const WALLRUN_CATCH_KEEP = 0.25;
/** How lateral the movement stick has to be before air control uses the strafe term. */
const AIR_STRAFE_LATERALITY = 0.85;
/** The tether counts as taut within this much slack. */
const GRAPPLE_TAUT_SLACK = 0.25;
/** Slide ends when speed decays below this fraction of the entry threshold. */
const SLIDE_EXIT_SPEED_FRAC = 0.5;

const DOWN: Vec3 = { x: 0, y: -1, z: 0 };

// ---------------------------------------------------------------------------
// Scratch.
//
// stepMovement is not re-entrant and never reads scratch it did not write during
// the same call, so module-level scratch is safe and keeps the hot path
// allocation-free (30 Hz per player on the server, plus up to a full input-buffer
// replay per frame on the client during reconciliation).
// ---------------------------------------------------------------------------

/** Flat basis from yaw. Sacred for the whole tick: only rebuilt at the top. */
const _fwd = v3();
const _right = v3();

const _wish = v3();
const _wishDir = v3();
const _tmp = v3();
const _tmp2 = v3();
const _tmp3 = v3();
const _eye = v3();
const _end = v3();
const _preVel = v3();
const _origPos = v3();
const _origVel = v3();
const _stepPos = v3();
const _stepVel = v3();
const _clip = v3();
const _crease = v3();
const _slopeDir = v3();
const _runDir = v3();
/** Ground normal for the current tick. Written before every read; never read across ticks. */
const _gn = v3(0, 1, 0);
/** Chosen grapple attach point for this tick. Module scope keeps the hot path allocation-free. */
const _grappleTarget = v3();

/** pickAnchor locals, kept separate so they can't stomp the yaw basis. */
const _aimDir = v3();
const _aVec = v3();
const _aDir = v3();
/** viewCameraRoll locals — that function is called from the renderer, outside the tick. */
const _rollRight = v3();

const _planes: Vec3[] = [v3(), v3(), v3(), v3(), v3(), v3()];

function newTrace(): TraceResult {
  return {
    fraction: 1,
    hit: false,
    normal: v3(0, 1, 0),
    point: v3(),
    surfaceId: -1,
    surface: SurfaceKind.Concrete,
    startSolid: false,
  };
}

function newRaycast(): RaycastResult {
  return { hit: false, distance: 0, point: v3(), normal: v3(0, 1, 0), surfaceId: -1, surface: SurfaceKind.Concrete };
}

const _trMove = newTrace();
const _trGround = newTrace();
const _trStepUp = newTrace();
const _trStepDown = newTrace();
const _trWallA = newTrace();
const _trWallB = newTrace();
const _trStuck = newTrace();
const _rc = newRaycast();
const _rcLos = newRaycast();
const _rcWall = newRaycast();
const _unstickPos = v3();
const _unstickOrigin = v3();
/** wallIsRunnable locals — kept off the shared scratch so the probe can never stomp the tick. */
const _wallEye = v3();
const _wallDir = v3();

/**
 * Escape directions for the last-resort unstick, tried in this order at each
 * distance. Up first (the overwhelmingly common wedge is a foot inside a lip), then
 * the horizontal compass, then down. Fixed order and fixed magnitudes keep the
 * result a pure function of position, which client prediction depends on.
 */
const H = Math.SQRT1_2;
const T = 1 / Math.sqrt(3);
const UNSTICK_DIRS: readonly Vec3[] = [
  { x: 0, y: 1, z: 0 },
  // Up-and-out. The wedge that actually happens in this arena is a foot under a deck
  // lip while a shoulder is inside the prop standing on it, and NOTHING purely
  // vertical or purely horizontal resolves that pair — only a diagonal does.
  { x: H, y: H, z: 0 },
  { x: -H, y: H, z: 0 },
  { x: 0, y: H, z: H },
  { x: 0, y: H, z: -H },
  { x: T, y: T, z: T },
  { x: -T, y: T, z: T },
  { x: T, y: T, z: -T },
  { x: -T, y: T, z: -T },
  { x: 1, y: 0, z: 0 },
  { x: -1, y: 0, z: 0 },
  { x: 0, y: 0, z: 1 },
  { x: 0, y: 0, z: -1 },
  { x: H, y: 0, z: H },
  { x: -H, y: 0, z: H },
  { x: H, y: 0, z: -H },
  { x: -H, y: 0, z: -H },
  { x: 0, y: -1, z: 0 },
];

/** Result of one collide-and-slide pass. */
interface MoveResult {
  /** True when a non-walkable plane stopped us — the trigger for a step-up retry. */
  blockedByWall: boolean;
  wallSurfaceId: number;
  hitFloor: boolean;
  floorNormal: Vec3;
  floorSurfaceId: number;
  floorSurface: SurfaceKind;
}

function newMoveResult(): MoveResult {
  return {
    blockedByWall: false,
    wallSurfaceId: -1,
    hitFloor: false,
    floorNormal: v3(0, 1, 0),
    floorSurfaceId: -1,
    floorSurface: SurfaceKind.Concrete,
  };
}

const _moveRes = newMoveResult();
const _stepRes = newMoveResult();

/** Per-tick ground state. */
interface GroundInfo {
  grounded: boolean;
  normal: Vec3;
  surfaceId: number;
  surface: SurfaceKind;
  /** Distance from the capsule base down to the surface. */
  gap: number;
}

const _ground: GroundInfo = {
  grounded: false,
  normal: v3(0, 1, 0),
  surfaceId: -1,
  surface: SurfaceKind.Concrete,
  gap: 0,
};

/**
 * Wall-runnable surface ids, cached per arena so the sim never walks the brush
 * list in the hot path.
 */
const wallrunCache = new WeakMap<ArenaData, Set<number>>();

function wallrunIds(arena: ArenaData): Set<number> {
  let s = wallrunCache.get(arena);
  if (!s) {
    s = new Set<number>();
    for (let i = 0; i < arena.brushes.length; i++) {
      const b = arena.brushes[i]!;
      if (b.wallrunnable && !b.decorative) s.add(b.id);
    }
    wallrunCache.set(arena, s);
  }
  return s;
}

/**
 * IS THIS A WALL YOU CAN RUN ON?
 *
 * It used to be a whitelist: only brushes the arena flagged `wallrunnable`. Only 66
 * of SKYLINE NINE's 278 solid brushes carry that flag, and the arena sweep found the
 * flag check rejecting a wall the player was already pressed against 263,000 times —
 * far and away the biggest single reason a wall-run refused to start. That is exactly
 * the playtest note: "the wall running is not really working how I expect."
 *
 * So the flag is now a HINT, not a gate, in the same way free aim replaced the
 * grapple's anchor whitelist. The rule the player gets instead is one they can see
 * from the geometry alone:
 *
 *     IF THE WALL IS TALLER THAN YOU, YOU CAN RUN ON IT.
 *
 * That single sentence is the whole policy, and it is also what keeps knee-high trim
 * out: a parapet, a curb or the raised lip of a skylight has a perfectly vertical
 * face that the capsule sweep is happy to report, and sticking to those would feel
 * insane. Checking eye height is cheap (one ray) and self-explaining.
 */
function wallIsRunnable(p: PlayerState, world: CollisionWorld, tr: TraceResult): boolean {
  const ids = wallrunIds(world.arena);
  if (ids.size > 0 && ids.has(tr.surfaceId)) return true;

  // Fire back into the wall from eye height. If the face is still there, the wall
  // covers you head to foot and it is honestly a wall.
  _wallDir.x = -tr.normal.x;
  _wallDir.y = 0;
  _wallDir.z = -tr.normal.z;
  if (lengthSq(_wallDir) < EPSILON) return false;
  normalize(_wallDir, _wallDir);
  _wallEye.x = p.pos.x;
  _wallEye.y = p.pos.y + PLAYER_EYE_HEIGHT;
  _wallEye.z = p.pos.z;
  const rc = world.raycast(_wallEye, _wallDir, PLAYER_RADIUS + WALL_PROBE_DIST + WALL_HEAD_SLACK, _rcWall);
  return rc.hit && Math.abs(rc.normal.y) <= WALL_TILT_SIN;
}

/** Horizontal speed measured IN the plane of a wall — the only speed a wall-run cares about. */
function speedAlongWall(vel: Vec3, normal: Vec3): number {
  const vn = vel.x * normal.x + vel.z * normal.z;
  const ax = vel.x - normal.x * vn;
  const az = vel.z - normal.z * vn;
  return Math.sqrt(ax * ax + az * az);
}

// ---------------------------------------------------------------------------
// Public helpers
// ---------------------------------------------------------------------------

/** The player's max input-driven ground speed, after the wound / unarmed modifiers. */
export function speedOf(p: PlayerState): number {
  let s = RUN_SPEED;
  if (p.healthState === HealthState.Wounded) s *= WOUNDED_SPEED_MULT;
  if (p.heldGun === -1) s *= UNARMED_SPEED_MULT;
  return s;
}

/** Eye/camera position — also the muzzle origin for hitscan, so it has to be exact. */
export function eyePosition(p: PlayerState, out: Vec3): Vec3 {
  out.x = p.pos.x;
  out.y = p.pos.y + (p.crouching ? PLAYER_CROUCH_EYE_HEIGHT : PLAYER_EYE_HEIGHT);
  out.z = p.pos.z;
  return out;
}

/**
 * Camera roll in radians. Positive means lean right.
 *
 * A pure function of player state — no hidden timers — so prediction, replay and
 * spectating all agree. It varies continuously (wall-run roll eases in off
 * `wallRunElapsedMs`, strafe roll tracks lateral velocity) and the renderer applies
 * CAMERA_ROLL_DAMP on top, which smooths the detach edge too.
 */
export function viewCameraRoll(p: PlayerState): number {
  if (p.healthState === HealthState.Dead) return 0;
  rightFromYaw(_rollRight, p.yaw);

  let roll = 0;
  let wallLean = 0;

  if (p.moveState === MoveState.WallRunning && p.wallNormal) {
    // wallNormal points away from the wall, toward the player: a wall on your right
    // has a normal pointing left, and you lean right, into it.
    //
    // The side used to come out of sign(), which meant the lean vanished at exactly
    // dot == 0 and flipped hard across it — so panning your view along the wall made
    // the horizon snap through level, which is the opposite of a readable signal.
    // A soft, saturating ramp holds full lean for any wall genuinely beside you and
    // eases through the ambiguous zone where "left" and "right" stop meaning anything.
    const side = dot(_rollRight, p.wallNormal);
    const ramp = smoothstep(p.wallRunElapsedMs / WALLRUN_ROLL_RAMP_MS);
    wallLean = -clamp(side / WALLRUN_ROLL_SIDE_SOFT, -1, 1) * WALLRUN_CAMERA_ROLL * ramp;
    roll += wallLean;
  }

  // Hard strafing and carving a slide lean the horizon the way a bike does. During a
  // wall-run it is faded out: the along-wall velocity has a lateral component of its
  // own that can point the other way, and two competing leans read as no lean at all.
  const strafeMax = WALLRUN_CAMERA_ROLL * STRAFE_ROLL_FRACTION * (1 - Math.abs(wallLean) / WALLRUN_CAMERA_ROLL);
  const lateral = _rollRight.x * p.vel.x + _rollRight.z * p.vel.z;
  roll += clamp(lateral / RUN_SPEED, -1, 1) * strafeMax;

  const limit = WALLRUN_CAMERA_ROLL + strafeMax;
  return clamp(roll, -limit, limit);
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function capsuleHeight(p: PlayerState): number {
  return p.crouching ? PLAYER_CROUCH_HEIGHT : PLAYER_HEIGHT;
}

/** Quake acceleration: only ever adds speed *along* wishDir, and never past wishSpeed. */
function accelerate(vel: Vec3, wishDir: Vec3, wishSpeed: number, accel: number, dt: number): void {
  if (wishSpeed <= 0) return;
  const current = dot(vel, wishDir);
  const addSpeed = wishSpeed - current;
  if (addSpeed <= 0) return;
  let accelSpeed = accel * wishSpeed * dt;
  if (accelSpeed > addSpeed) accelSpeed = addSpeed;
  addScaled(vel, vel, wishDir, accelSpeed);
}

function applyFriction(vel: Vec3, friction: number, dt: number): void {
  if (friction <= 0) return;
  const speed = length(vel);
  if (speed < EPSILON) {
    set(vel, 0, 0, 0);
    return;
  }
  const control = speed < STOP_SPEED ? STOP_SPEED : speed;
  let newSpeed = speed - control * friction * dt;
  if (newSpeed < 0) newSpeed = 0;
  scale(vel, vel, newSpeed / speed);
}

/** Rotate the horizontal part of `vel` toward `dir` by at most `maxRad`, preserving speed. */
function steerHorizontal(vel: Vec3, dir: Vec3, maxRad: number): void {
  const hs = lengthXZ(vel);
  if (hs < EPSILON || maxRad <= 0) return;
  const dl = Math.sqrt(dir.x * dir.x + dir.z * dir.z);
  if (dl < EPSILON) return;
  const vx = vel.x / hs;
  const vz = vel.z / hs;
  const dx = dir.x / dl;
  const dz = dir.z / dl;
  const ang = Math.acos(clamp(vx * dx + vz * dz, -1, 1));
  if (ang < EPSILON) return;
  const s = vx * dz - vz * dx >= 0 ? -1 : 1; // 2D cross picks the short way round
  const t = ang <= maxRad ? ang : maxRad;
  const c = Math.cos(t);
  const sn = Math.sin(t) * s;
  vel.x = (vx * c - vz * sn) * hs;
  vel.z = (vx * sn + vz * c) * hs;
}

function sanitize(vel: Vec3): void {
  if (!Number.isFinite(vel.x) || !Number.isFinite(vel.y) || !Number.isFinite(vel.z)) {
    set(vel, 0, 0, 0);
    return;
  }
  const sp = length(vel);
  if (sp > MAX_SANE_SPEED) scale(vel, vel, MAX_SANE_SPEED / sp);
}

/** Unit downhill direction of `normal`, written into `out`. Returns sin(slope angle). */
function downhill(out: Vec3, normal: Vec3): number {
  projectOnPlane(out, DOWN, normal);
  const m = length(out);
  if (m < EPSILON) {
    set(out, 0, 0, 0);
    return 0;
  }
  scale(out, out, 1 / m);
  return m;
}

// ---------------------------------------------------------------------------
// Collide and slide
// ---------------------------------------------------------------------------

/**
 * Dig the capsule out of anything it is inside, in place. Returns false only if the
 * geometry has genuinely swallowed it.
 *
 * A zero-length capsule trace reports nothing except start-solid, and when it does
 * its normal is the shallowest axis of escape from the DEEPEST overlap — exactly the
 * minimum-translation direction. Pushing along it and re-querying resolves stacked
 * overlaps one at a time, so a body wedged in the seam between two brushes walks out
 * of both instead of oscillating between them.
 *
 * If that loop fails to converge (possible when two solids' shallowest axes point at
 * each other) we fall back to a fixed ladder of directions and take the nearest one
 * that reaches free space. Every step is a pure function of position and geometry —
 * no timers, no history — so the server and the client's replay always agree.
 */
function depenetrate(pos: Vec3, height: number, world: CollisionWorld): boolean {
  copy(_unstickOrigin, pos);

  for (let i = 0; i < UNSTICK_ITERS; i++) {
    const tr = world.traceCapsule(pos, pos, PLAYER_RADIUS, height, _trStuck);
    if (!tr.startSolid) return true;
    // Steps grow, so a deep overlap is cleared without a long stall at 12 mm a go.
    addScaled(pos, pos, tr.normal, UNSTICK_STEP * (i + 1));
  }
  if (!world.isSolid(pos, PLAYER_RADIUS, height)) return true;

  // The loop can also oscillate: a tank's side face pushes you one way, the corner of
  // the ramp beside it pushes you back, and every round trip sinks you a little
  // further into the deck below. Rewind to where we started — it is the shallowest
  // point we know of — and search outward instead.
  copy(pos, _unstickOrigin);
  for (let d = 0; d < UNSTICK_DISTANCES.length; d++) {
    const dist = UNSTICK_DISTANCES[d]!;
    for (let k = 0; k < UNSTICK_DIRS.length; k++) {
      const dir = UNSTICK_DIRS[k]!;
      _unstickPos.x = pos.x + dir.x * dist;
      _unstickPos.y = pos.y + dir.y * dist;
      _unstickPos.z = pos.z + dir.z * dist;
      if (!world.isSolid(_unstickPos, PLAYER_RADIUS, height)) {
        copy(pos, _unstickPos);
        return true;
      }
    }
  }
  return false;
}

/**
 * Clip `vel` so it stops pushing into any of the first `n` recorded planes.
 *
 * Four stages, each a strictly weaker claim than the last: one plane, then a crease
 * between two, then alternating projection onto all of them, and only then a stop.
 * The third stage is the one that matters for feel — it is what turns "three brushes
 * met near your feet, you now have zero velocity" into "you slid out of the seam".
 */
function clipToPlanes(vel: Vec3, n: number): void {
  for (let i = 0; i < n; i++) {
    clipVelocity(_clip, vel, _planes[i]!, OVERBOUNCE);
    let ok = true;
    for (let j = 0; j < n; j++) {
      if (j === i) continue;
      if (dot(_clip, _planes[j]!) < 0) {
        ok = false;
        break;
      }
    }
    if (ok) {
      copy(vel, _clip);
      return;
    }
  }
  // Two planes at once — slide along their crease.
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      cross(_crease, _planes[i]!, _planes[j]!);
      if (lengthSq(_crease) < EPSILON) continue;
      normalize(_crease, _crease);
      scale(_clip, _crease, dot(vel, _crease));
      let ok = true;
      for (let k = 0; k < n; k++) {
        if (dot(_clip, _planes[k]!) < -EPSILON) {
          ok = false;
          break;
        }
      }
      if (ok) {
        copy(vel, _clip);
        return;
      }
    }
  }
  // Three or more planes and no single crease satisfies them all. Do NOT stop dead:
  // alternating projection onto the half-spaces converges on the closest velocity the
  // geometry actually permits, which is zero only when the pocket is genuinely closed
  // in the direction you are travelling. Anything else is momentum we get to keep.
  copy(_clip, vel);
  for (let pass = 0; pass < CLIP_RELAX_PASSES; pass++) {
    let violated = false;
    for (let i = 0; i < n; i++) {
      if (dot(_clip, _planes[i]!) < 0) {
        clipVelocity(_clip, _clip, _planes[i]!, 1);
        violated = true;
      }
    }
    if (!violated) break;
  }
  for (let i = 0; i < n; i++) {
    if (dot(_clip, _planes[i]!) < -EPSILON) {
      set(vel, 0, 0, 0);
      return;
    }
  }
  copy(vel, _clip);
}

/**
 * Sweep the capsule through `dt` seconds of `vel`, sliding along whatever it hits.
 * Mutates `pos` and `vel` in place.
 */
function slideMove(pos: Vec3, vel: Vec3, height: number, dt: number, world: CollisionWorld, res: MoveResult): void {
  res.blockedByWall = false;
  res.hitFloor = false;
  res.wallSurfaceId = -1;
  res.floorSurfaceId = -1;

  let timeLeft = dt;
  let numPlanes = 0;

  for (let bump = 0; bump < MAX_BUMPS; bump++) {
    if (timeLeft <= EPSILON) break;
    if (lengthSq(vel) < EPSILON) break;

    addScaled(_end, pos, vel, timeLeft);
    const tr = world.traceCapsule(pos, _end, PLAYER_RADIUS, height, _trMove);

    if (tr.startSolid) {
      // Wedged inside geometry. This used to lift 3 cm and abandon the rest of the
      // tick, which is precisely the hitch that reads as "I got stuck on nothing":
      // a 30 Hz player at run speed loses 40 cm of travel, and if the lift did not
      // clear the overlap it happened again the next tick, and the next. Dig out
      // along the true minimum-translation direction and carry on with the move.
      if (!depenetrate(pos, height, world)) break;
      continue;
    }

    const f = clamp01(tr.fraction);
    if (f > 0) addScaled(pos, pos, vel, timeLeft * f);
    if (!tr.hit) break;

    timeLeft -= timeLeft * f;
    addScaled(pos, pos, tr.normal, SURFACE_OFFSET);

    if (tr.normal.y >= GROUND_NORMAL_MIN) {
      res.hitFloor = true;
      copy(res.floorNormal, tr.normal);
      res.floorSurfaceId = tr.surfaceId;
      res.floorSurface = tr.surface;
    } else if (tr.normal.y > -GROUND_NORMAL_MIN) {
      res.blockedByWall = true;
      res.wallSurfaceId = tr.surfaceId;
    }

    // Record the plane, skipping near-duplicates so the crease solver stays sane.
    let dup = false;
    for (let i = 0; i < numPlanes; i++) {
      if (dot(_planes[i]!, tr.normal) > 0.99) {
        dup = true;
        break;
      }
    }
    if (!dup && numPlanes < _planes.length) {
      copy(_planes[numPlanes]!, tr.normal);
      numPlanes++;
    }

    clipToPlanes(vel, numPlanes);
  }
}

/**
 * `slideMove` plus stair stepping: run the move twice — flat, and from a stepped-up
 * start — and keep whichever covered more ground. No explicit step trigger needed,
 * so stairs, curbs and arena trim are all climbed silently.
 */
function stepMove(p: PlayerState, height: number, dt: number, world: CollisionWorld, allowStep: boolean): MoveResult {
  copy(_origPos, p.pos);
  copy(_origVel, p.vel);

  slideMove(p.pos, p.vel, height, dt, world, _moveRes);

  if (!allowStep || !_moveRes.blockedByWall) return _moveRes;

  // Stash the flat result so we can fall back to it.
  copy(_stepPos, p.pos);
  copy(_stepVel, p.vel);
  const flatDx = p.pos.x - _origPos.x;
  const flatDz = p.pos.z - _origPos.z;
  const flatDist = flatDx * flatDx + flatDz * flatDz;

  copy(p.pos, _origPos);
  copy(p.vel, _origVel);
  set(_end, _origPos.x, _origPos.y + STEP_HEIGHT, _origPos.z);
  const up = world.traceCapsule(_origPos, _end, PLAYER_RADIUS, height, _trStepUp);
  const climbed = STEP_HEIGHT * clamp01(up.fraction) - (up.hit ? SURFACE_OFFSET : 0);
  if (climbed <= SURFACE_OFFSET) {
    copy(p.pos, _stepPos);
    copy(p.vel, _stepVel);
    return _moveRes;
  }
  p.pos.y = _origPos.y + climbed;

  slideMove(p.pos, p.vel, height, dt, world, _stepRes);

  // Drop back down onto whatever we stepped onto.
  const dropDist = climbed + GROUND_PROBE;
  set(_end, p.pos.x, p.pos.y - dropDist, p.pos.z);
  const down = world.traceCapsule(p.pos, _end, PLAYER_RADIUS, height, _trStepDown);
  if (!down.hit || down.startSolid || down.normal.y < GROUND_NORMAL_MIN) {
    // Nothing walkable up there; the flat move was the honest one.
    copy(p.pos, _stepPos);
    copy(p.vel, _stepVel);
    return _moveRes;
  }
  p.pos.y -= dropDist * clamp01(down.fraction);
  p.pos.y += SURFACE_OFFSET;

  const stepDx = p.pos.x - _origPos.x;
  const stepDz = p.pos.z - _origPos.z;
  if (stepDx * stepDx + stepDz * stepDz <= flatDist) {
    copy(p.pos, _stepPos);
    copy(p.vel, _stepVel);
    return _moveRes;
  }

  _stepRes.hitFloor = true;
  copy(_stepRes.floorNormal, down.normal);
  _stepRes.floorSurfaceId = down.surfaceId;
  _stepRes.floorSurface = down.surface;
  return _stepRes;
}

/** Probe straight down for standable ground. Fills and returns `_ground`. */
function probeGround(pos: Vec3, height: number, dist: number, world: CollisionWorld): GroundInfo {
  set(_end, pos.x, pos.y - dist, pos.z);
  const tr = world.traceCapsule(pos, _end, PLAYER_RADIUS, height, _trGround);
  if (tr.hit && !tr.startSolid && tr.normal.y >= GROUND_NORMAL_MIN) {
    _ground.grounded = true;
    copy(_ground.normal, tr.normal);
    _ground.surfaceId = tr.surfaceId;
    _ground.surface = tr.surface;
    _ground.gap = dist * clamp01(tr.fraction);
  } else {
    _ground.grounded = false;
    set(_ground.normal, 0, 1, 0);
    _ground.surfaceId = -1;
    _ground.surface = SurfaceKind.Concrete;
    _ground.gap = dist;
  }
  return _ground;
}

// ---------------------------------------------------------------------------
// Slide
// ---------------------------------------------------------------------------

function enterSlide(p: PlayerState, boost: boolean, events: GameEvent[]): void {
  p.moveState = MoveState.Sliding;
  p.crouching = true;
  p.slideElapsedMs = 0;

  const hs = lengthXZ(p.vel);
  if (hs > EPSILON) {
    let target = hs;
    if (boost) target += SLIDE_BOOST;
    // A dash that converts into a slide inside the window cashes out as slide speed.
    if (lengthSq(p.dashDir) > EPSILON && p.dashElapsedMs <= DASH_DURATION_MS + DASH_TO_SLIDE_WINDOW_MS) {
      target *= DASH_TO_SLIDE_BONUS;
      set(p.dashDir, 0, 0, 0);
    }
    const k = target / hs;
    p.vel.x *= k;
    p.vel.z *= k;
  }
  events.push({ t: 'slidestart', player: p.id });
}

function exitSlide(p: PlayerState): void {
  if (p.moveState === MoveState.Sliding) p.moveState = p.grounded ? MoveState.Grounded : MoveState.Airborne;
  p.slideElapsedMs = 0;
}

// ---------------------------------------------------------------------------
// Wall-run
// ---------------------------------------------------------------------------

/**
 * Sweep sideways for a runnable wall. `preferSide` is -1 or 1 to test a single side
 * (used while already attached), or 0 to test both and take the closer hit.
 *
 * The sweep is along the view's right vector rather than the velocity's, and that is
 * deliberate: the capsule is fat, so any probe with even a sliver of a component
 * toward a wall you are beside will find it, while a view-relative probe keeps the
 * "the wall is on my left / on my right" relationship the camera roll advertises.
 */
function probeWall(
  p: PlayerState,
  height: number,
  world: CollisionWorld,
  preferSide: number,
  allowSameSurface: boolean,
): TraceResult | null {
  const hs = lengthXZ(p.vel);
  const vx = hs > EPSILON ? p.vel.x / hs : 0;
  const vz = hs > EPSILON ? p.vel.z / hs : 0;
  const holding = preferSide !== 0;
  const reach = PLAYER_RADIUS + (holding ? WALL_HOLD_PROBE_DIST : WALL_PROBE_DIST);

  let best: TraceResult | null = null;
  let bestFrac = 2;

  for (let i = 0; i < 2; i++) {
    const side = i === 0 ? -1 : 1;
    if (preferSide !== 0 && side !== preferSide) continue;

    set(_end, p.pos.x + _right.x * side * reach, p.pos.y, p.pos.z + _right.z * side * reach);
    const tr = world.traceCapsule(p.pos, _end, PLAYER_RADIUS, height, i === 0 ? _trWallA : _trWallB);
    if (!tr.hit || tr.startSolid) continue;
    if (Math.abs(tr.normal.y) > WALL_TILT_SIN) continue; // near-vertical only
    if (!allowSameSurface && tr.surfaceId === p.wallSurfaceId && WALLRUN_SAME_SURFACE_LOCKOUT) {
      // The wall you just KICKED OFF is locked out until you touch ground or another
      // surface — that's what stops infinite laddering up one flat wall. A run that
      // merely petered out clears the id instead (see detachWall), because refusing
      // to re-grab a wall you never jumped off reads as the game ignoring you.
      continue;
    }
    if (hs > EPSILON && !holding) {
      // On entry the wall has to be beside you, not something you are burying
      // yourself in. Once attached the test is dropped: the into-wall component of
      // your velocity is clipped every tick anyway, so all it could ever do is
      // eject you at an inside corner — the exact moment you want the run to carry.
      const into = -(vx * tr.normal.x + vz * tr.normal.z);
      if (into > WALLRUN_MAX_INTO_WALL) continue;
    }
    if (!wallIsRunnable(p, world, tr)) continue;
    if (tr.fraction < bestFrac) {
      bestFrac = tr.fraction;
      best = tr;
    }
  }
  return best;
}

/**
 * Leave the wall.
 *
 * `kicked` distinguishes the two exits, and they are not the same event:
 *  - kicked (a wall-jump): keep `wallSurfaceId`, so this wall stays locked out until
 *    you touch ground or another surface. That is the anti-laddering rule.
 *  - not kicked (the run ran out of speed, angle or wall): clear the id and take a
 *    short re-attach cooldown instead. Being told "no" by a wall you never jumped
 *    off is the single most confusing thing a wall-run can do.
 */
function detachWall(p: PlayerState, events: GameEvent[], kicked: boolean): void {
  const wasRunning = p.moveState === MoveState.WallRunning;
  p.wallNormal = null;
  if (kicked) {
    p.wallRunElapsedMs = 0;
  } else {
    p.wallSurfaceId = -1;
    // Negative wallRunElapsedMs is the re-attach cooldown, counting back up to zero.
    p.wallRunElapsedMs = -WALLRUN_REATTACH_COOLDOWN_MS;
  }
  if (wasRunning) {
    p.moveState = MoveState.Airborne;
    events.push({ t: 'wallrun', player: p.id, start: false });
  }
}

// ---------------------------------------------------------------------------
// Grapple
// ---------------------------------------------------------------------------

function releaseGrapple(p: PlayerState, events: GameEvent[], boosted: boolean): void {
  if (boosted) {
    const sp = length(p.vel);
    if (sp > EPSILON) {
      // Tension becomes launch speed. The cap is raised by the boost itself, so a
      // well-timed release genuinely beats the tether's steady-state speed.
      const cap = Math.max(GRAPPLE_MAX_SPEED * GRAPPLE_RELEASE_BOOST, sp);
      const target = Math.min(sp * GRAPPLE_RELEASE_BOOST, cap);
      scale(p.vel, p.vel, target / sp);
    }
  }
  p.grappleAnchorId = -1;
  p.grapplePoint = null;
  p.grappleHookProgress = 0;
  p.grappleElapsedMs = 0;
  p.grappleLength = 0;
  p.grappleCooldownMs = GRAPPLE_COOLDOWN_MS;
  if (p.moveState === MoveState.Grappling) p.moveState = p.grounded ? MoveState.Grounded : MoveState.Airborne;
  events.push({ t: 'grapplerelease', player: p.id, boosted });
}

/** Nearest anchor to the crosshair inside the aim cone, with line of sight. -1 if none. */
function pickAnchor(p: PlayerState, world: CollisionWorld): number {
  const anchors = world.arena.anchors;
  if (anchors.length === 0) return -1;

  eyePosition(p, _eye);
  dirFromYawPitch(_aimDir, p.yaw, p.pitch);

  let bestId = -1;
  let bestAngle = GRAPPLE_AIM_CONE;
  let bestDist = Infinity;

  for (let i = 0; i < anchors.length; i++) {
    const a = anchors[i]!;
    sub(_aVec, a.pos, _eye);
    const d = length(_aVec);
    if (d > GRAPPLE_RANGE || d < EPSILON) continue;
    scale(_aDir, _aVec, 1 / d);
    const ang = Math.acos(clamp(dot(_aDir, _aimDir), -1, 1));
    if (ang > GRAPPLE_AIM_CONE) continue;
    // Strict comparison plus array order keeps the choice deterministic everywhere.
    if (ang > bestAngle || (ang === bestAngle && d >= bestDist)) continue;
    const rc = world.raycast(_eye, _aDir, d, _rc);
    if (rc.hit && rc.distance < d - GRAPPLE_MIN_LENGTH * 0.25) continue;
    bestAngle = ang;
    bestDist = d;
    bestId = a.id;
  }
  return bestId;
}

function anchorPos(world: CollisionWorld, id: number): Vec3 | null {
  const anchors = world.arena.anchors;
  for (let i = 0; i < anchors.length; i++) {
    if (anchors[i]!.id === id) return anchors[i]!.pos;
  }
  return null;
}

/** Where a grapple is attached: a placed anchor's position, or the stored surface hit point. */
function grappleTargetPos(p: PlayerState, world: CollisionWorld): Vec3 | null {
  if (p.grappleAnchorId === GRAPPLE_SURFACE) return p.grapplePoint;
  return anchorPos(world, p.grappleAnchorId);
}

/**
 * Choose what this grapple attaches to, writing the point into `out`.
 *
 * Free aim first-class: you grapple whatever you are looking at. A placed anchor wins only when
 * one is genuinely near the crosshair, because anchors hang in open air over the void where a
 * raycast finds nothing — they buy swings that free-aim physically cannot.
 *
 * Returns the anchor id, GRAPPLE_SURFACE for a world surface, or GRAPPLE_NONE for no target.
 */
function pickGrappleTarget(p: PlayerState, world: CollisionWorld, out: Vec3): number {
  const id = pickAnchor(p, world);
  if (id >= 0) {
    const ap = anchorPos(world, id);
    if (ap) {
      copy(out, ap);
      return id;
    }
  }

  eyePosition(p, _eye);
  dirFromYawPitch(_aimDir, p.yaw, p.pitch);
  const rc = world.raycast(_eye, _aimDir, GRAPPLE_RANGE, _rc);
  // Too close and the tether would yank you into the wall you are standing against.
  if (rc.hit && rc.distance >= GRAPPLE_MIN_LENGTH) {
    copy(out, rc.point);
    return GRAPPLE_SURFACE;
  }
  return GRAPPLE_NONE;
}

// ---------------------------------------------------------------------------
// The tick
// ---------------------------------------------------------------------------

export function stepMovement(p: PlayerState, cmd: InputCommand, world: CollisionWorld, events: GameEvent[]): void {
  if (p.healthState === HealthState.Dead) {
    set(p.vel, 0, 0, 0);
    p.moveState = MoveState.Airborne;
    p.grounded = false;
    p.wallNormal = null;
    p.grappleAnchorId = -1;
    p.grapplePoint = null;
    p.grappleHookProgress = 0;
    return;
  }

  const dt = clamp(cmd.dt, 0, MAX_STEP_DT);
  if (dt <= 0) return;
  const dtMs = dt * 1000;

  // --- view -----------------------------------------------------------------
  p.yaw = cmd.yaw;
  p.pitch = clamp(cmd.pitch, -PITCH_LIMIT, PITCH_LIMIT);
  forwardFromYaw(_fwd, p.yaw);
  rightFromYaw(_right, p.yaw);

  // --- buttons --------------------------------------------------------------
  const jumpHeld = hasBtn(cmd.buttons, Btn.Jump);
  const crouchHeld = hasBtn(cmd.buttons, Btn.Crouch);
  const dashHeld = hasBtn(cmd.buttons, Btn.Dash);
  const grappleHeld = hasBtn(cmd.buttons, Btn.Grapple);

  // Inputs arrive as held-state, so press edges are derived from stored state:
  //   crouch  -> p.crouching holds last tick's resolved crouch
  //   jump    -> latched inside p.jumpBufferMs (encoding below)
  const crouchPressed = crouchHeld && !p.crouching;

  // jumpBufferMs encoding:
  //   > 0  a press is buffered and counting down
  //   = 0  released / idle, ready to accept a new press edge
  //   < 0  press already consumed (or expired) while still held; waits for release
  if (!jumpHeld) {
    p.jumpBufferMs = 0;
  } else if (p.jumpBufferMs === 0) {
    p.jumpBufferMs = JUMP_BUFFER_MS;
  } else if (p.jumpBufferMs > 0) {
    p.jumpBufferMs -= dtMs;
    if (p.jumpBufferMs <= 0) p.jumpBufferMs = -1;
  }

  // --- crouch / stand -------------------------------------------------------
  if (crouchHeld) {
    p.crouching = true;
  } else if (p.crouching) {
    const wasSliding = p.moveState === MoveState.Sliding;
    const committed = wasSliding && p.slideElapsedMs < SLIDE_MIN_MS; // minimum slide commitment
    if (!committed && !world.isSolid(p.pos, PLAYER_RADIUS, PLAYER_HEIGHT)) {
      // Never stand up inside geometry.
      p.crouching = false;
      if (wasSliding) exitSlide(p);
    }
  }

  let height = capsuleHeight(p);

  // --- ground state ---------------------------------------------------------
  // Re-derived from geometry every tick rather than trusted from the last one, so a
  // teleport (round start), a crouch height change or a replayed input can't lie.
  const g0 = probeGround(p.pos, height, GROUND_PROBE, world);
  p.grounded = g0.grounded && p.vel.y <= GROUND_VY_TOLERANCE;
  copy(_gn, g0.normal);
  let groundSurface = g0.surface;
  const wasGrounded = p.grounded;

  // Hold-to-bunnyhop: on the ground a held jump re-arms the buffer, so chaining
  // slide-hops never demands frame-perfect re-presses. In the air the latch stands,
  // which is exactly what stops a held button from eating the double jump.
  if (jumpHeld && p.grounded && p.jumpBufferMs < 0) p.jumpBufferMs = JUMP_BUFFER_MS;
  let jumpQueued = p.jumpBufferMs > 0;

  // --- timers ---------------------------------------------------------------
  if (p.grappleCooldownMs > 0) p.grappleCooldownMs = Math.max(0, p.grappleCooldownMs - dtMs);

  if (p.grounded) p.coyoteMs = COYOTE_TIME_MS;
  else if (p.coyoteMs > 0) p.coyoteMs = Math.max(0, p.coyoteMs - dtMs);

  // Dash charge economy. Unarmed players carry one extra charge (DESIGN.md §1).
  const maxDash = p.heldGun === -1 ? DASH_CHARGES_UNARMED : DASH_CHARGES;
  if (p.dashCharges > maxDash) p.dashCharges = maxDash;
  if (DASH_GROUND_REFILL && p.grounded) {
    if (p.dashCharges < maxDash) {
      p.dashCharges = maxDash;
      p.dashCooldownMs = 0;
    }
  } else if (p.dashCharges < maxDash) {
    p.dashCooldownMs -= dtMs;
    if (p.dashCooldownMs <= 0) {
      p.dashCharges++;
      p.dashCooldownMs = p.dashCharges < maxDash ? DASH_RECHARGE_MS : 0;
    }
  } else {
    p.dashCooldownMs = 0;
  }

  if (lengthSq(p.dashDir) > EPSILON) {
    p.dashElapsedMs += dtMs;
    // Clearing dashDir ends both the dash and its dash-to-slide window.
    if (p.dashElapsedMs > DASH_DURATION_MS + DASH_TO_SLIDE_WINDOW_MS) set(p.dashDir, 0, 0, 0);
  }
  let dashing = lengthSq(p.dashDir) > EPSILON && p.dashElapsedMs < DASH_DURATION_MS;

  if (p.moveState === MoveState.Sliding) {
    p.slideElapsedMs += dtMs;
  } else if (p.slideElapsedMs < 0) {
    // Negative = crouch buffered in mid-air, counting up over SURF_LAND_GRACE_MS.
    p.slideElapsedMs = Math.min(0, p.slideElapsedMs + dtMs);
  }

  if (p.moveState === MoveState.WallRunning) {
    p.wallRunElapsedMs += dtMs;
  } else if (p.wallRunElapsedMs < 0) {
    p.wallRunElapsedMs = Math.min(0, p.wallRunElapsedMs + dtMs);
  }

  // Buffer a mid-air crouch press for the surf landing refund.
  if (crouchPressed && !p.grounded) p.slideElapsedMs = -SURF_LAND_GRACE_MS;

  // --- wish direction -------------------------------------------------------
  const moveX = clamp(cmd.moveX, -1, 1);
  const moveZ = clamp(cmd.moveZ, -1, 1);
  set(_wish, _right.x * moveX + _fwd.x * moveZ, 0, _right.z * moveX + _fwd.z * moveZ);
  const wishLen = lengthXZ(_wish);
  const hasWish = wishLen > EPSILON;
  if (hasWish) scale(_wishDir, _wish, 1 / wishLen);
  else set(_wishDir, 0, 0, 0);
  const wishSpeed = speedOf(p) * Math.min(wishLen, 1);

  let gravityScale = 1;

  // --- grapple --------------------------------------------------------------
  if (p.grappleAnchorId !== GRAPPLE_NONE) {
    const ap = grappleTargetPos(p, world);
    if (!ap || !p.grapplePoint) {
      releaseGrapple(p, events, false);
    } else {
      if (ap !== p.grapplePoint) copy(p.grapplePoint, ap);
      eyePosition(p, _eye);

      if (p.grappleHookProgress < 1) {
        // The hook is still travelling. The player is completely unaffected — that
        // is what makes an incoming grapple readable and dodgeable.
        const d = distance(_eye, ap);
        p.grappleHookProgress += d > EPSILON ? (GRAPPLE_HOOK_SPEED * dt) / d : 1;
        if (p.grappleHookProgress >= 1) {
          p.grappleHookProgress = 1;
          p.grappleElapsedMs = 0;
          p.grappleLength = Math.max(GRAPPLE_MIN_LENGTH, d);
          events.push({ t: 'grappleattach', player: p.id, anchor: p.grappleAnchorId });
        }
        if (!grappleHeld) releaseGrapple(p, events, false);
      } else {
        p.grappleElapsedMs += dtMs;
        sub(_tmp, ap, _eye);
        const dist = length(_tmp);
        if (dist < EPSILON) {
          releaseGrapple(p, events, false);
        } else {
          scale(_tmp2, _tmp, 1 / dist); // unit vector toward the anchor

          // The apex of a swing is the moment vertical velocity crosses zero, so
          // "released within GRAPPLE_RELEASE_WINDOW_MS of the apex" is exactly
          // |vy| <= g * window. Stateless, deterministic, physically honest.
          const apexVy = Math.abs(GRAVITY) * (GRAPPLE_RELEASE_WINDOW_MS / 1000);
          const taut = dist >= p.grappleLength - GRAPPLE_TAUT_SLACK;
          const nearApex =
            taut &&
            _eye.y < ap.y &&
            Math.abs(p.vel.y) <= apexVy &&
            lengthXZ(p.vel) >= RUN_SPEED &&
            p.grappleElapsedMs >= GRAPPLE_RELEASE_WINDOW_MS;

          const rc = world.raycast(_eye, _tmp2, dist, _rcLos);
          const losBlocked = rc.hit && rc.distance < dist - GRAPPLE_MIN_LENGTH * 0.25;

          if (!grappleHeld || p.grappleElapsedMs >= GRAPPLE_MAX_MS || dist <= GRAPPLE_MIN_LENGTH || losBlocked) {
            releaseGrapple(p, events, nearApex && !losBlocked);
          } else {
            const before = length(p.vel);
            addScaled(p.vel, p.vel, _tmp2, GRAPPLE_PULL_ACCEL * dt); // reel in
            // The tether only ever shortens; no slack accumulates behind you.
            if (dist < p.grappleLength) p.grappleLength = Math.max(GRAPPLE_MIN_LENGTH, dist);
            if (dist > p.grappleLength) {
              // Spring + radial damping once taut: this is what makes it a swing.
              const stretch = dist - p.grappleLength;
              addScaled(p.vel, p.vel, _tmp2, GRAPPLE_SPRING_K * stretch * dt);
              const radial = dot(p.vel, _tmp2);
              addScaled(p.vel, p.vel, _tmp2, -radial * GRAPPLE_DAMPING * dt);
            }
            // Clamp only what the grapple added; incoming momentum is never eaten.
            const after = length(p.vel);
            const cap = Math.max(GRAPPLE_MAX_SPEED, before);
            if (after > cap && after > EPSILON) scale(p.vel, p.vel, cap / after);
            if (p.grounded && p.vel.y > 0) p.grounded = false;
          }
        }
      }
    }
  } else if (grappleHeld && p.grappleCooldownMs <= 0) {
    const id = pickGrappleTarget(p, world, _grappleTarget);
    if (id !== GRAPPLE_NONE) {
      const ap = _grappleTarget;
      eyePosition(p, _eye);
      p.grappleAnchorId = id;
      p.grapplePoint = { x: ap.x, y: ap.y, z: ap.z };
      p.grappleHookProgress = EPSILON;
      p.grappleElapsedMs = 0;
      p.grappleLength = Math.max(GRAPPLE_MIN_LENGTH, distance(_eye, ap));
      events.push({ t: 'grapplefire', player: p.id, anchor: id });
    }
  }

  const grappling = p.grappleAnchorId !== GRAPPLE_NONE && p.grappleHookProgress >= 1;

  // --- wall-run -------------------------------------------------------------
  let wallRunning = p.moveState === MoveState.WallRunning && p.wallNormal !== null;

  if (wallRunning && p.wallNormal) {
    const side = dot(_right, p.wallNormal) > 0 ? -1 : 1;
    const tr = probeWall(p, height, world, side, true);
    const hSpeed = lengthXZ(p.vel);
    const lost =
      tr === null ||
      p.grounded ||
      dashing ||
      p.wallRunElapsedMs >= WALLRUN_MAX_MS ||
      hSpeed < WALLRUN_MIN_SPEED * WALLRUN_KEEP_SPEED_FRAC ||
      moveZ < 0 ||
      crouchPressed;
    if (lost) {
      detachWall(p, events, false);
      wallRunning = false;
    } else {
      copy(p.wallNormal, tr.normal);
      p.wallSurfaceId = tr.surfaceId;
    }
  }

  if (!wallRunning && !p.grounded && !dashing && p.wallRunElapsedMs >= 0) {
    // Entry gates, in the order a player would understand them:
    //   1. don't hold back      2. don't hold crouch
    //   3. be off the floor — unless you are still CLIMBING, because a jump straight
    //      at a wall is the most obvious wall-run input there is and refusing it for
    //      the first 140 ms of the jump is invisible and infuriating
    //   4. find a wall          5. be moving along that wall, not merely at it
    if (moveZ > -WALLRUN_MIN_FORWARD && !crouchHeld) {
      const low = p.vel.y <= 0 && world.raycast(p.pos, DOWN, WALLRUN_MIN_HEIGHT, _rc).hit;
      if (!low) {
        const tr = probeWall(p, height, world, 0, false);
        if (tr) {
          const along = speedAlongWall(p.vel, tr.normal);
          const fast = along >= WALLRUN_MIN_SPEED * WALLRUN_ENTRY_SPEED_FRAC || lengthXZ(p.vel) >= WALLRUN_MIN_SPEED;
          if (fast) {
            p.moveState = MoveState.WallRunning;
            p.wallNormal = { x: tr.normal.x, y: tr.normal.y, z: tr.normal.z };
            p.wallSurfaceId = tr.surfaceId;
            p.wallRunElapsedMs = 0;
            p.jumpsRemaining = 1;
            if (p.vel.y < 0) p.vel.y *= WALLRUN_CATCH_KEEP; // catch the fall, keep the climb
            wallRunning = true;
            events.push({ t: 'wallrun', player: p.id, start: true });
          }
        }
      }
    }
  }

  // --- jump -----------------------------------------------------------------
  let jumpedThisTick = false;

  if (jumpQueued) {
    if (wallRunning && p.wallNormal) {
      // Kick out and up while preserving the along-wall run.
      projectOnPlane(_tmp, p.vel, p.wallNormal);
      const alongSpeed = Math.sqrt(_tmp.x * _tmp.x + _tmp.z * _tmp.z);
      p.vel.x = _tmp.x * WALLJUMP_FORWARD_KEEP + p.wallNormal.x * WALLJUMP_PUSH_VELOCITY;
      p.vel.z = _tmp.z * WALLJUMP_FORWARD_KEEP + p.wallNormal.z * WALLJUMP_PUSH_VELOCITY;
      p.vel.y = Math.max(p.vel.y, WALLJUMP_UP_VELOCITY);
      // The kick is an ADDITION, never a tax. WALLJUMP_FORWARD_KEEP redirects some of
      // the run into the push, but if the sum still comes out slower than the run you
      // arrived with, the wall just ate momentum — the one thing DESIGN.md §3 forbids.
      // Scale the result back up to what you had; the direction is the wall's choice,
      // the speed stays yours.
      const outSpeed = lengthXZ(p.vel);
      if (outSpeed > EPSILON && outSpeed < alongSpeed) {
        const k = alongSpeed / outSpeed;
        p.vel.x *= k;
        p.vel.z *= k;
      }
      detachWall(p, events, true);
      wallRunning = false;
      p.grounded = false;
      p.jumpsRemaining = 1;
      p.jumpBufferMs = -1;
      jumpQueued = false;
      jumpedThisTick = true;
      events.push({ t: 'walljump', player: p.id });
    } else if (p.grounded || p.coyoteMs > 0) {
      if (p.moveState === MoveState.Sliding) exitSlide(p); // slide-hop keeps every bit of speed
      p.vel.y = Math.max(p.vel.y, JUMP_VELOCITY);
      p.grounded = false;
      p.coyoteMs = 0;
      p.jumpsRemaining = 1;
      p.jumpBufferMs = -1;
      jumpQueued = false;
      jumpedThisTick = true;
      p.moveState = MoveState.Airborne;
      events.push({ t: 'jump', player: p.id, double: false });
    } else if (p.jumpsRemaining > 0) {
      p.vel.y = Math.max(p.vel.y, DOUBLE_JUMP_VELOCITY);
      p.jumpsRemaining--;
      p.jumpBufferMs = -1;
      jumpQueued = false;
      jumpedThisTick = true;
      events.push({ t: 'jump', player: p.id, double: true });
    }
  }

  // --- dash -----------------------------------------------------------------
  // A live dashDir doubles as the "dashed recently" latch, which is what stops a
  // held dash button from dumping every charge on consecutive ticks.
  if (dashHeld && !dashing && p.dashCharges > 0 && lengthSq(p.dashDir) <= EPSILON) {
    if (hasWish) copy(p.dashDir, _wishDir);
    else copy(p.dashDir, _fwd);
    p.dashDir.y = 0;
    normalize(p.dashDir, p.dashDir);
    if (lengthSq(p.dashDir) > EPSILON) {
      p.dashCharges--;
      if (p.dashCooldownMs <= 0) p.dashCooldownMs = DASH_RECHARGE_MS;
      p.dashElapsedMs = 0;
      dashing = true;
      p.vel.y *= DASH_VERTICAL_DAMP; // dashes are mostly horizontal
      const along = dot(p.vel, p.dashDir);
      if (along < DASH_SPEED) addScaled(p.vel, p.vel, p.dashDir, DASH_SPEED - along);
      if (wallRunning) {
        // Dashing off a wall is not a kick — you may want that wall again.
        detachWall(p, events, false);
        wallRunning = false;
      }
      if (p.moveState === MoveState.Sliding) exitSlide(p);
      p.moveState = MoveState.Dashing;
      events.push({ t: 'dash', player: p.id, dir: { x: p.dashDir.x, y: p.dashDir.y, z: p.dashDir.z } });
    } else {
      set(p.dashDir, 0, 0, 0);
    }
  }

  if (dashing) {
    p.moveState = MoveState.Dashing;
    // Hold the dash vector without ever reducing perpendicular momentum.
    const along = dot(p.vel, p.dashDir);
    if (along < DASH_SPEED) addScaled(p.vel, p.vel, p.dashDir, DASH_SPEED - along);
    gravityScale = DASH_VERTICAL_DAMP;
  }

  // --- slide ----------------------------------------------------------------
  if (p.moveState === MoveState.Sliding) {
    if (!p.grounded) {
      // Ran off the lip: airborne now, still crouched, resumes on touchdown.
      p.moveState = MoveState.Airborne;
      p.slideElapsedMs = 0;
    } else if (
      p.slideElapsedMs >= SLIDE_MIN_MS &&
      (!crouchHeld || lengthXZ(p.vel) < SLIDE_MIN_SPEED * SLIDE_EXIT_SPEED_FRAC)
    ) {
      exitSlide(p);
    }
  } else if (!dashing && !wallRunning && p.grounded && crouchPressed && lengthXZ(p.vel) >= SLIDE_MIN_SPEED) {
    enterSlide(p, true, events);
  }

  const sliding = p.moveState === MoveState.Sliding;
  height = capsuleHeight(p);

  // --- acceleration ---------------------------------------------------------
  if (wallRunning && p.wallNormal) {
    const n = p.wallNormal;
    if (dot(p.vel, n) < 0) clipVelocity(p.vel, p.vel, n, 1); // kill only the into-wall part

    // Run direction: where you're looking, flattened onto the wall. Looking back
    // along the wall must never brake you, so fall back to the velocity direction.
    projectOnPlane(_runDir, _fwd, n);
    _runDir.y = 0;
    set(_tmp3, p.vel.x, 0, p.vel.z);
    projectOnPlane(_tmp2, _tmp3, n);
    _tmp2.y = 0;
    const haveVelDir = lengthSq(_tmp2) > EPSILON;
    if (haveVelDir) normalize(_tmp2, _tmp2);
    if (lengthSq(_runDir) < EPSILON) {
      if (haveVelDir) copy(_runDir, _tmp2);
      else set(_runDir, 0, 0, 0);
    } else {
      normalize(_runDir, _runDir);
      if (haveVelDir && dot(_runDir, _tmp2) < 0) copy(_runDir, _tmp2);
    }
    if (lengthSq(_runDir) > EPSILON) accelerate(p.vel, _runDir, WALLRUN_MAX_SPEED, WALLRUN_ACCEL, dt);

    // Stick, so a bump in the trim doesn't eject you.
    addScaled(p.vel, p.vel, n, -WALLRUN_STICK_FORCE * dt);
    // Gravity ramps from almost nothing back to full across the run's lifetime.
    gravityScale = lerpN(WALLRUN_GRAVITY_SCALE, 1, smoothstep(p.wallRunElapsedMs / WALLRUN_MAX_MS));
  } else if (dashing) {
    // A dash is a committed vector: no friction, no air control.
  } else if (sliding) {
    // Friction scales with the normal force, so the steeper the ramp the less it
    // bites. Without this the terminal slide speed is the same on every slope and
    // "surfing builds speed" is a lie: 30*sin(t) would just balance 1.4*v.
    applyFriction(p.vel, SLIDE_FRICTION * _gn.y, dt);
    // Surf: gravity along the ground plane. Flat ground contributes exactly nothing,
    // a steep ramp genuinely builds speed.
    const slopeSin = downhill(_slopeDir, _gn);
    if (slopeSin > EPSILON && lengthXZ(p.vel) < SLIDE_MAX_SPEED) {
      addScaled(p.vel, p.vel, _slopeDir, SLIDE_SLOPE_ACCEL * slopeSin * dt);
    }
    if (hasWish) steerHorizontal(p.vel, _wishDir, SLIDE_STEER_RATE * dt); // reduced authority
    projectOnPlane(p.vel, p.vel, _gn);
    gravityScale = 0;
  } else if (p.grounded && !jumpedThisTick) {
    applyFriction(p.vel, GROUND_FRICTION, dt);
    if (hasWish) {
      projectOnPlane(_tmp, _wishDir, _gn); // run along the slope, not into it
      if (lengthSq(_tmp) > EPSILON) {
        normalize(_tmp, _tmp);
        accelerate(p.vel, _tmp, wishSpeed, GROUND_ACCEL, dt);
      }
    }
    projectOnPlane(p.vel, p.vel, _gn);
    gravityScale = 0;
  } else {
    if (AIR_FRICTION > 0) applyFriction(p.vel, AIR_FRICTION, dt);
    if (hasWish) {
      const lateral = Math.abs(moveX);
      const total = lateral + Math.abs(moveZ);
      const laterality = total > EPSILON ? lateral / total : 0;
      if (laterality >= AIR_STRAFE_LATERALITY) {
        // The strafe term: huge accel against a tiny wishspeed cap. Turning the mouse
        // into a held strafe key is how you *gain* speed in the air.
        accelerate(p.vel, _wishDir, Math.min(wishSpeed, AIR_SPEED_CAP), AIR_STRAFE_ACCEL, dt);
      } else {
        // Holding forward can only bring you up to run speed, never past it.
        accelerate(p.vel, _wishDir, wishSpeed, AIR_ACCEL, dt);
      }
    }
  }

  // --- gravity --------------------------------------------------------------
  if (gravityScale > 0) p.vel.y += GRAVITY * gravityScale * dt;

  sanitize(p.vel);

  // --- move -----------------------------------------------------------------
  copy(_preVel, p.vel);
  // Step-up is not a stairs feature, it is a momentum feature, so it applies in the
  // air too: 477 of the hard stops in the arena sweep were a bunny-hop or a fall
  // clipping a knee-high lip and dying against it. Gated on not still climbing, so
  // a mistimed jump into a ledge still reads as a mistimed jump.
  const allowStep = !jumpedThisTick && (wasGrounded || p.vel.y <= AIR_STEP_MAX_RISE);
  const res = stepMove(p, height, dt, world, allowStep);

  // --- categorize -----------------------------------------------------------
  let g = probeGround(p.pos, height, GROUND_PROBE, world);
  let groundedNow = g.grounded && p.vel.y <= GROUND_VY_TOLERANCE && !jumpedThisTick;

  if (!groundedNow && wasGrounded && !jumpedThisTick && p.vel.y <= GROUND_VY_TOLERANCE) {
    // Ground snap: walking down a slope or a step must not turn into a hop.
    g = probeGround(p.pos, height, STEP_HEIGHT, world);
    if (g.grounded) {
      p.pos.y -= g.gap - SURFACE_OFFSET;
      groundedNow = true;
    }
  }
  if (groundedNow) {
    copy(_gn, g.normal);
    groundSurface = g.surface;
  } else if (res.hitFloor) {
    copy(_gn, res.floorNormal);
    groundSurface = res.floorSurface;
  }

  const justLanded = groundedNow && !wasGrounded;
  p.grounded = groundedNow;

  if (groundedNow) {
    p.jumpsRemaining = 1;
    p.coyoteMs = COYOTE_TIME_MS;
    if (wallRunning) {
      detachWall(p, events, false);
      wallRunning = false;
    }
    // Ground contact clears the same-surface lockout and the re-attach cooldown. It
    // has to run AFTER the detach, which would otherwise re-arm the cooldown.
    p.wallSurfaceId = -1;
    if (p.wallRunElapsedMs < 0) p.wallRunElapsedMs = 0;
    if (DASH_GROUND_REFILL && p.dashCharges < maxDash) {
      p.dashCharges = maxDash;
      p.dashCooldownMs = 0;
    }
  }

  if (justLanded) {
    const impact = Math.abs(dot(_preVel, _gn));
    const preSpeed = length(_preVel);
    projectOnPlane(p.vel, p.vel, _gn);

    // Surf landing: hitting a downslope with a slide input inside the grace window
    // refunds the speed the landing would otherwise have eaten. Momentum is sacred.
    const surfEligible = crouchHeld || p.slideElapsedMs < 0;
    let refunded = false;
    if (surfEligible && preSpeed > SLIDE_MIN_SPEED) {
      const slopeSin = downhill(_slopeDir, _gn);
      if (slopeSin > EPSILON && _preVel.x * _slopeDir.x + _preVel.z * _slopeDir.z > 0) {
        const target = preSpeed * SURF_LAND_REFUND;
        const now = length(p.vel);
        if (now > EPSILON) {
          if (target > now) {
            scale(p.vel, p.vel, target / now);
            refunded = true;
          }
        } else {
          scale(p.vel, _slopeDir, target);
          refunded = true;
        }
      }
    }

    events.push({ t: 'land', player: p.id, speed: impact, surface: groundSurface });

    // Resume or enter a slide on touchdown. The entry boost only applies to a fresh
    // input — a refunded surf landing is already carrying its reward.
    const freshCrouch = crouchPressed || p.slideElapsedMs < 0;
    if (p.slideElapsedMs < 0) p.slideElapsedMs = 0;
    if (crouchHeld && !dashing && p.moveState !== MoveState.Sliding && lengthXZ(p.vel) >= SLIDE_MIN_SPEED) {
      enterSlide(p, freshCrouch && !refunded, events);
    }

    // A jump buffered just before touchdown fires now instead of a tick late.
    if (jumpQueued) {
      if (p.moveState === MoveState.Sliding) exitSlide(p);
      p.vel.y = Math.max(p.vel.y, JUMP_VELOCITY);
      p.grounded = false;
      groundedNow = false;
      p.coyoteMs = 0;
      p.jumpsRemaining = 1;
      p.jumpBufferMs = -1;
      jumpQueued = false;
      p.moveState = MoveState.Airborne;
      events.push({ t: 'jump', player: p.id, double: false });
    }
  }

  sanitize(p.vel);

  // --- kill plane -----------------------------------------------------------
  if (p.pos.y < world.arena.killPlaneY) {
    if (wallRunning) detachWall(p, events, false);
    p.healthState = HealthState.Dead;
    p.health = 0;
    p.deaths++;
    set(p.vel, 0, 0, 0);
    p.moveState = MoveState.Airborne;
    p.grounded = false;
    p.crouching = false;
    p.wallNormal = null;
    p.grappleAnchorId = -1;
    p.grapplePoint = null;
    p.grappleHookProgress = 0;
    p.grappleElapsedMs = 0;
    // Self-elimination: killer === victim. match.ts scores it, the client reads it.
    events.push({ t: 'kill', killer: p.id, victim: p.id, zone: HitZone.None, gunId: -1 });
    return;
  }

  // --- final movement state -------------------------------------------------
  if (dashing) p.moveState = MoveState.Dashing;
  else if (wallRunning) p.moveState = MoveState.WallRunning;
  else if (p.moveState === MoveState.Sliding && p.grounded) p.moveState = MoveState.Sliding;
  else if (grappling && p.grappleAnchorId >= 0) p.moveState = MoveState.Grappling;
  else p.moveState = p.grounded ? MoveState.Grounded : MoveState.Airborne;
}
